import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency } from '../../utils/formatters.js';

function outstanding(inv) {
  return Math.max(0, (Number(inv.totalAmount) || 0) - (Number(inv.paidAmount) || 0) - (Number(inv.creditedAmount) || 0));
}

export function BankMatchModal({ open, onClose, transaction }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [invoiceId, setInvoiceId] = useState('');
  const [error, setError] = useState(null);

  const invoiceType = transaction?.direction === 'DEBIT' ? 'PURCHASE' : 'SALES';

  const candidates = useQuery({
    queryKey: ['finance', 'invoices', invoiceType, 'POSTED'],
    queryFn: () => financeApi.listInvoices({ type: invoiceType, status: 'POSTED' }).then((r) => r.items),
    enabled: open,
  });

  const invoices = candidates.data || [];

  // Pre-select the suggested invoice (by number) once candidates load / modal opens.
  useEffect(() => {
    if (!open) {
      setInvoiceId('');
      setError(null);
      return;
    }
    if (transaction?.suggestedInvoiceNumber && invoices.length > 0) {
      const match = invoices.find((inv) => inv.invoiceNumber === transaction.suggestedInvoiceNumber);
      if (match) {
        setInvoiceId(String(match.id));
      }
    }
  }, [open, transaction, invoices]);

  const match = useMutation({
    mutationFn: (id) => financeApi.matchBankTransaction(transaction.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'bank'] });
      queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!invoiceId) {
      setError(t('bank.match.selectRequired'));
      return;
    }
    match.mutate(invoiceId);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('bank.match.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={match.isPending}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={match.isPending || candidates.isLoading}>
            {match.isPending ? `${t('bank.match.confirm')}…` : t('bank.match.confirm')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {transaction && (
          <p className="muted" style={{ margin: 0 }}>
            {formatCurrency(transaction.amount)} · {transaction.counterparty || transaction.reference || '-'}
          </p>
        )}
        <label className="field">
          <span className="field-label">{t('bank.match.invoiceLabel')}</span>
          <select
            className="input"
            value={invoiceId}
            onChange={(e) => setInvoiceId(e.target.value)}
            disabled={candidates.isLoading}
          >
            <option value="">{candidates.isLoading ? `${t('bank.loading')}…` : t('bank.match.placeholder')}</option>
            {invoices.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.invoiceNumber} · {t('bank.match.open')} {formatCurrency(outstanding(inv))}
              </option>
            ))}
          </select>
        </label>
        {!candidates.isLoading && invoices.length === 0 && (
          <p className="muted" style={{ margin: 0 }}>{t('bank.match.noCandidates')}</p>
        )}
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
